import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { GlobeAltIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { server } from '../../server';
import ImageCropperModal from '../Image/ImageCropperModal';
import {
  IMAGE_ACCEPT_WITH_HEIC,
  getImagePreparationErrorMessage,
  prepareImageFileForUpload,
} from '../../utils/imageUploadPreparation';

const RoomCreationForm = ({ onClose }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [privacy, setPrivacy] = useState('public');
  const [avatarImage, setAvatarImage] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState('');
  const [bannerImage, setBannerImage] = useState(null);
  const [bannerPreview, setBannerPreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const currentProfile = useSelector((state) => state.user.currentProfile) || sessionStorage.getItem('currentProfile') || 'User';
  const [showCropper, setShowCropper] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [cropTarget, setCropTarget] = useState('banner');
  const [isPreparingImage, setIsPreparingImage] = useState(false);

  const prepareImageForCropper = async (file, target) => {
    setError('');
    setIsPreparingImage(true);

    try {
      const preparedFile = await prepareImageFileForUpload(file);
      setSelectedFile(preparedFile);
      setCropTarget(target);
      setShowCropper(true);
    } catch (error) {
      console.error(`Error preparing room ${target}:`, error);
      setSelectedFile(null);
      setShowCropper(false);
      setError(getImagePreparationErrorMessage(error));
    } finally {
      setIsPreparingImage(false);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';

    if (file) {
      await prepareImageForCropper(file, 'avatar');
    }
  };

  const handleBannerChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';

    if (file) {
      await prepareImageForCropper(file, 'banner');
    }
  };

  const handleCroppedImage = (croppedFile) => {
    if (cropTarget === 'avatar') {
      setAvatarImage(croppedFile);
    } else {
      setBannerImage(croppedFile);
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      if (cropTarget === 'avatar') {
        setAvatarPreview(reader.result);
      } else {
        setBannerPreview(reader.result);
      }
    };
    reader.readAsDataURL(croppedFile);

    setShowCropper(false);
    setSelectedFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const formData = new FormData();
    formData.append('name', name);
    formData.append('description', description);
    formData.append('privacy', privacy);
    if (avatarImage) {
      formData.append('avatarImage', avatarImage);
    }
    if (bannerImage) {
      formData.append('bannerImage', bannerImage);
    }

    try {
      const response = await axios.post(`${server}/room/rooms/create`, formData, {
        withCredentials: true,
        headers: {
          'current-profile': currentProfile,
        },
      });

      if (response.status === 201) {
        const { room } = response.data; 
        const { handle } = room; 
        alert('Room created successfully!');
        navigate(`/rooms/${handle}`);
      }
    } catch (err) {
      console.error('Error creating room:', err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          'An error occurred while creating the room.'
      );
    } finally {
      setLoading(false);
    }
  };

  const privacyOptions = [
    {
      value: 'public',
      title: 'Public',
      description: 'Open for discovery, posting, comments, events, and ads.',
      Icon: GlobeAltIcon,
    },
    {
      value: 'private',
      title: 'Private',
      description: 'Visible to invited members and owners only.',
      Icon: LockClosedIcon,
    },
  ];

  return (
    <div className="room-creation-form-container">
      <h2>Create a New Community</h2>
      <form onSubmit={handleSubmit} className="room-creation-form">
        <div className="room-creation-form-group">
          <label htmlFor="name">Room Name</label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="room-creation-form-input"
          />
        </div>

        <div className="room-creation-form-group">
          <label>Community Access</label>
          <div className="room-creation-form-privacy-grid">
            {privacyOptions.map(({ value, title, description: optionDescription, Icon }) => (
              <button
                key={value}
                type="button"
                className={`room-creation-form-privacy-option${privacy === value ? ' active' : ''}`}
                onClick={() => setPrivacy(value)}
                aria-pressed={privacy === value}
              >
                <Icon className="room-creation-form-privacy-icon" aria-hidden="true" />
                <span className="room-creation-form-privacy-title">{title}</span>
                <span className="room-creation-form-privacy-description">{optionDescription}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="room-creation-form-group">
          <label htmlFor="description">Room Description</label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows="4"
            maxLength="500"
            placeholder="Enter a brief description of the room (max 500 characters)"
            className="room-creation-form-input"
          />
        </div>

        <div className="room-creation-form-group">
          <label htmlFor="community-avatar">Community Logo</label>
          <input
            type="file"
            id="community-avatar"
            accept={IMAGE_ACCEPT_WITH_HEIC}
            onChange={handleAvatarChange}
            disabled={isPreparingImage || loading}
            className="room-creation-form-input"
          />
          {avatarPreview && (
            <div className="room-creation-form-avatar-preview">
              <img src={avatarPreview} alt="Community Logo Preview" />
            </div>
          )}
        </div>

        <div className="room-creation-form-group">
          <label htmlFor="banner">Room Banner</label>
          <input
            type="file"
            id="banner"
            accept={IMAGE_ACCEPT_WITH_HEIC}
            onChange={handleBannerChange}
            disabled={isPreparingImage || loading}
            className="room-creation-form-input"
          />
          {bannerPreview && (
            <div className="room-creation-form-banner-preview">
              <img src={bannerPreview} alt="Banner Preview" />
            </div>
          )}
        </div>

        {error && <p className="room-creation-form-error">{error}</p>}

        <div className="room-creation-form-actions">
          <button
            type="button"
            className="room-creation-form-cancel-button"
            onClick={onClose}
            disabled={loading || isPreparingImage}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="room-creation-form-button"
            disabled={loading || isPreparingImage}
          >
            {loading ? 'Creating...' : isPreparingImage ? 'Preparing Image...' : 'Create Room'}
          </button>
        </div>
      </form>

      {showCropper && selectedFile && (
        <ImageCropperModal
          initialImage={selectedFile}
          updateAvatar={handleCroppedImage}
          closeModal={() => setShowCropper(false)}
          aspectRatio={cropTarget === 'avatar' ? 1 : 4 / 1}
          minWidth={250}
          isAvatar={cropTarget === 'avatar'}
          isPost={false}
          isLogo={false}
        />
      )}
    </div>
  );
};

export default RoomCreationForm;


